/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import Classnames from 'classnames';
import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';
import { Field } from 'redux-form';
import { Popover, TextStyle, ActionList } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

// import { RenderColorPicker2 } from 'src/components/views/common/forms/render-color-picker-2';
import { RenderColorCustomPicker } from 'src/components/views/common/forms/render-color-custom-picker';
import { ELEMENTS, MAP_COLOR_NAME, MENU_ITEM_VALUE } from 'src/constants/theme-settings.js';
import IconDB from 'src/assets/images/shopify-integration-settings/database.svg?react';
import IconDots from 'src/assets/images/shopify-integration-settings/h-dots.svg?react';
import { updateElementColorRespective } from 'src/helpers/shopify-integration-settings-helper';
import { RGBAToHex, isHexWithAlpha } from 'src/helpers/color-helper';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ColorPickerPaletteItem extends Component<ComposedProps> {
  state = {
    activeColorPicker: false,
    activeColorPickerPalette: false,

    activeMenuSettingColor: false,
    activeColorPickerInMenu: false,
    activeColorPickerPaletteInMenu: false
  };

  componentDidMount() {
    Emitter.on(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR, this.handleHideColorPicker);
  }

  componentWillUnmount() {
    Emitter.off(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR, this.handleHideColorPicker);
  }

  handleHideColorPicker = () => {
    this.setState(
      {
        activeColorPicker: false,
        activeColorPickerPalette: false,
        activeMenuSettingColor: false
      },
      () => {
        setTimeout(() => {
          this.setState({
            activeColorPickerInMenu: false,
            activeColorPickerPaletteInMenu: false
          });
        }, 250);
      }
    );
  };

  handleToggleColorPicker = () => {
    this.setState(({ activeColorPicker }) => ({
      activeColorPicker: !activeColorPicker
    }));
  };

  handleToggleColorPickerPalette = () => {
    this.setState(({ activeColorPickerPalette }) => ({
      activeColorPickerPalette: !activeColorPickerPalette
    }));
  };

  handleToggleMenuSettingColor = () => {
    if (this.blockCloseMenuSettingColor) {
      this.blockCloseMenuSettingColor = false;
      return;
    }
    this.setState(({ activeMenuSettingColor }) => ({
      activeMenuSettingColor: !activeMenuSettingColor
    }));
    this.handleCloseSelectInMenu();
  };

  handleChangeColor = (color: Object) => {
    const { updateField, elementKey, formValues } = this.props;
    const temporaryColor = _.cloneDeep(_.get(formValues, elementKey));
    // updateField(`${elementKey}.value`, String(color.hex).toUpperCase());
    updateField(`${elementKey}.referenceTo`, '');

    // Temporarily synchronous data for updating
    _.set(temporaryColor, 'value', RGBAToHex(color.rgb).toUpperCase());
    _.set(temporaryColor, 'referenceTo', '');
    const { setting, isChangedPagination, paginationSetting } = updateElementColorRespective(
      temporaryColor,
      formValues?.setting,
      formValues?.pagination
    );
    updateField('setting', setting);
    if (isChangedPagination) {
      updateField('pagination', paginationSetting);
    }
  };

  handleSelectTemplateColor = (templateColor: Object) => {
    const { elementKey, updateField, formValues } = this.props;
    const temporaryColor = _.cloneDeep(_.get(formValues, elementKey));

    updateField(`${elementKey}.value`, String(templateColor.value).toUpperCase());
    updateField(`${elementKey}.referenceTo`, templateColor.name);

    // Temporarily synchronous data for updating
    _.set(temporaryColor, 'value', String(templateColor.value).toUpperCase());
    _.set(temporaryColor, 'referenceTo', templateColor.name);
    const { setting, isChangedPagination, paginationSetting } = updateElementColorRespective(
      temporaryColor,
      formValues?.setting,
      formValues?.pagination
    );
    updateField('setting', setting);
    if (isChangedPagination) {
      updateField('pagination', paginationSetting);
    }

    if (this.state.activeMenuSettingColor) {
      this.setState({
        activeMenuSettingColor: false
      });
      this.handleCloseSelectInMenu();
    } else {
      this.setState({
        activeColorPickerPalette: false
      });
    }
  };

  handleEditStyle = () => {
    this.props.updateFieldReducer('activeMenuItem', MENU_ITEM_VALUE.COLOR);
  };

  handleReplaceTemplateColor = () => {
    this.blockCloseMenuSettingColor = true;
    this.setState({
      activeColorPickerPaletteInMenu: true
    });
  };

  handleSelectColor = () => {
    this.blockCloseMenuSettingColor = true;
    this.setState({
      activeColorPickerInMenu: true
    });
  };

  handleCloseSelectInMenu = () => {
    setTimeout(() => {
      this.setState({
        activeColorPickerInMenu: false,
        activeColorPickerPaletteInMenu: false
      });
    }, 250);
  };

  getTemplateColor = () => {
    const { formValues } = this.props;
    const baseColor1 = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_1}`, '');
    const baseColor2 = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_2}`, '');
    const baseColor3 = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_3}`, '');
    const baseColor4 = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_4}`, '');
    const baseColor5 = _.get(formValues, `colors.${ELEMENTS.COLOR_BASE_5}`, '');
    const accentColor1 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_1}`, '');
    const accentColor2 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_2}`, '');
    const accentColor3 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_3}`, '');
    const accentColor4 = _.get(formValues, `colors.${ELEMENTS.ACCENT_BASE_4}`, '');
    const colors = [
      {
        name: 'baseColor1',
        value: baseColor1
      },
      {
        name: 'baseColor2',
        value: baseColor2
      },
      {
        name: 'baseColor3',
        value: baseColor3
      },
      {
        name: 'baseColor4',
        value: baseColor4
      },
      {
        name: 'baseColor5',
        value: baseColor5
      },
      {
        name: 'accentColor1',
        value: accentColor1
      },
      {
        name: 'accentColor2',
        value: accentColor2
      },
      {
        name: 'accentColor3',
        value: accentColor3
      },
      {
        name: 'accentColor4',
        value: accentColor4
      }
    ].filter(item => Boolean(item.value));
    return colors;
  };

  renderSelectTemplateColor = () => {
    const { i18n, elementKey, formValues } = this.props;
    const templateColors = this.getTemplateColor();
    const referenceValue = _.get(formValues, `${elementKey}.referenceTo`, '');

    return (
      <div className="color-palette-picker">
        <div className="color-palette-picker__header">
          <p className="title">
            {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Palette')}
          </p>
        </div>
        <div className="color-palette-picker__subheader">
          <p className="title">
            {i18n.translate(
              'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Palette_Template'
            )}
          </p>
        </div>
        <div className="color-palette-picker__body">
          {!!templateColors.length &&
            templateColors.map((item, index) => {
              const colorName = i18n.translate(MAP_COLOR_NAME[item.name]);
              return (
                <div
                  key={index}
                  className={Classnames('color-palette-picker-item', {
                    active: referenceValue === item.name
                  })}
                  style={{ background: item.value }}
                  data-tip={JSON.stringify({ colorName, value: item.value })}
                  data-for={`color-picker-item-${elementKey}.value`}
                  onClick={() => this.handleSelectTemplateColor(item)}></div>
              );
            })}

          <ReactTooltip
            id={`color-picker-item-${elementKey}.value`}
            place="top"
            effect="solid"
            type="light"
            getContent={dataTip => {
              if (_.isEmpty(dataTip)) {
                return null;
              }

              let data = {};
              try {
                data = JSON.parse(dataTip);
              } catch (e) {
                console.log('ERROR', e);
              }
              const { colorName, value } = data;
              return (
                <div>
                  <p>{colorName}</p>
                  <TextStyle variation="subdued">{value}</TextStyle>
                </div>
              );
            }}
          />
        </div>
      </div>
    );
  };

  renderPopoverMenuContent = () => {
    const { i18n, elementKey, formValues } = this.props;
    const { activeColorPickerPaletteInMenu, activeColorPickerInMenu } = this.state;

    if (activeColorPickerPaletteInMenu) {
      return this.renderSelectTemplateColor();
    }

    if (activeColorPickerInMenu) {
      const colorValue = _.get(formValues, `${elementKey}.value`, '');

      return (
        <Field
          name={`${elementKey}.value`}
          component={RenderColorCustomPicker}
          color={colorValue}
          onChange={this.handleChangeColor}
          normalize={color => {
            return String(RGBAToHex(color.rgb)).toUpperCase();
          }}
        />
      );
    }

    return (
      <div className="shopify-integration-setting-popover-menu">
        <Popover.Pane>
          <ActionList
            items={[
              {
                content: i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Menu_EditStyle'
                ),
                onAction: this.handleEditStyle
              },
              {
                content: i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Menu_Replace'
                ),
                onAction: this.handleReplaceTemplateColor
              },
              {
                content: i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Menu_Select'
                ),
                onAction: this.handleSelectColor
              }
            ]}
          />
        </Popover.Pane>
      </div>
    );
  };

  render() {
    const { activeColorPicker, activeColorPickerPalette, activeMenuSettingColor } = this.state;
    const { i18n, elementKey, formValues, label } = this.props;

    const colorValue = _.get(formValues, `${elementKey}.value`, '');
    const referenceValue = _.get(formValues, `${elementKey}.referenceTo`, '');

    let colorValueWithoutA = colorValue;
    if (isHexWithAlpha(colorValue)) {
      colorValueWithoutA = colorValue.slice(0, colorValue.length - 2);
    }

    const activatorClassName = Classnames('color-picker-item__activator', {
      'color-picker-item__activator--active': activeColorPickerPalette || activeMenuSettingColor
    });
    const activator = (
      <div
        className={activatorClassName}
        onClick={this.handleToggleColorPickerPalette}
        data-tip={i18n.translate(
          'AdminHTML.ShopifyIntegrationSettings.Form.advancedColors_Tooltip_ConnectPalette'
        )}
        data-for="color-picker-connect">
        <IconDB />
      </div>
    );

    const activatorMenu = (
      <div className={activatorClassName} onClick={this.handleToggleMenuSettingColor}>
        <IconDots />
      </div>
    );

    const colorPickerItemClass = Classnames('color-picker-item color-picker-item--circle', {
      'connected-palette': !!referenceValue,
      'not-connected-palette': !referenceValue
    });

    return (
      <div className={colorPickerItemClass}>
        <div className="color-picker-info">
          <Popover
            active={activeColorPicker}
            activator={
              <div
                className="color-picker-item__box-color"
                style={{ cursor: 'pointer', backgroundColor: colorValueWithoutA }}
                onClick={this.handleToggleColorPicker}
              />
            }
            onClose={this.handleToggleColorPicker}
            preferredAlignment="left">
            <Field
              name={`${elementKey}.value`}
              component={RenderColorCustomPicker}
              color={colorValue}
              onChange={this.handleChangeColor}
              normalize={color => {
                return String(RGBAToHex(color.rgb)).toUpperCase();
              }}
            />
          </Popover>

          <div>
            <p className="color-picker-item__name">{label}</p>
            <p
              className={Classnames('color-picker-item__value', {
                'color-picker-item__value--hex': !referenceValue
              })}>
              {referenceValue ? i18n.translate(MAP_COLOR_NAME[referenceValue]) : colorValueWithoutA}
            </p>
          </div>
        </div>

        <Popover
          active={activeColorPickerPalette}
          activator={activator}
          onClose={this.handleToggleColorPickerPalette}
          preferredAlignment="left">
          {this.renderSelectTemplateColor()}
        </Popover>

        <Popover
          active={activeMenuSettingColor}
          activator={activatorMenu}
          onClose={this.handleToggleMenuSettingColor}
          preferredAlignment="left">
          {this.renderPopoverMenuContent()}
        </Popover>
      </div>
    );
  }
}

export default withI18n()(ColorPickerPaletteItem);
